import React from 'react'
import CurrencySelect from './CurrencySelect'

/**
 * Amount input with a currency selector embedded on the left, styled as one
 * continuous field (matches the "GBP ⌄ | Enter amount" pattern).
 *
 *   <CurrencyInput
 *     currency={currency}
 *     onCurrencyChange={setCurrency}
 *     value={amount}
 *     onValueChange={setAmount}
 *     currencies={[
 *       { value: 'GBP', label: 'GBP' },
 *       { value: 'USD', label: 'USD' },
 *       { value: 'EUR', label: 'EUR' },
 *     ]}
 *   />
 *
 * Controlled component throughout — `currency`/`value` + their `onChange`
 * pairs are both required so the amount and the currency code stay in sync
 * with whatever holds them (form state, MobX store, etc.).
 */
const CurrencyInput = ({
  currency,
  onCurrencyChange,
  value,
  onValueChange,
  onValueBlur,
  placeholder = '0.00',
  disabled = false,
  className = '',
  inputProps = {},
  selectProps = {},
  ...rest
}) => {
  const handleAmountChange = (e) => {
    const raw = e.target.value
    // Allow empty, digits, and a single decimal point — keeps the field a
    // controlled numeric-ish string without fighting the user mid-keystroke.
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
      onValueChange?.(raw)
    }
  }

  const handleAmountBlur = (e) => {
    onValueBlur()
  }

  return (
    <div
      className={['currency-input', disabled ? 'currency-input--disabled' : '', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <div className='currency-input__select-wrap'>
        <CurrencySelect
          currency={currency}
          disabled={disabled}
          onChange={(val) => onCurrencyChange(val)}
          {...selectProps}
        />
      </div>

      <div className='currency-input__divider' />

      <input
        type='text'
        inputMode='decimal'
        className='currency-input__amount'
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={handleAmountChange}
        onBlur={handleAmountBlur}
        aria-label='Amount'
        {...inputProps}
      />
    </div>
  )
}

export default CurrencyInput
